import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, DataTable, Badge } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, Building2, ShieldCheck } from "lucide-react";

export default function Hotels() {
  const [hotels, setHotels] = useState([]);
  const [depts, setDepts] = useState([]);
  const [deptOpen, setDeptOpen] = useState(false);
  const [deptForm, setDeptForm] = useState({ name: "", code: "", hotel_id: "" });

  const load = async () => {
    const [h, d] = await Promise.all([api.get("/hotels"), api.get("/departments")]);
    setHotels(h.data); setDepts(d.data);
  };
  useEffect(() => { load(); }, []);

  const createDept = async () => {
    try { await api.post("/departments", deptForm); toast.success("Department created"); setDeptOpen(false); setDeptForm({ name: "", code: "", hotel_id: "" }); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const removeDept = async (id) => { if (!window.confirm("Delete department?")) return; try { await api.delete(`/departments/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div data-testid="hotels-page">
      <PageHeader title="Hotels & Departments" subtitle="Org Structure" />
      <Tabs defaultValue="hotels" className="space-y-4">
        <TabsList className="bg-hocp-bgSubtle border border-hocp-border">
          <TabsTrigger value="hotels" data-testid="tab-hotels">Hotels ({hotels.length})</TabsTrigger>
          <TabsTrigger value="departments" data-testid="tab-departments">Departments ({depts.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="hotels" className="space-y-4">
          <div
            className="flex items-start gap-3 rounded-lg border border-blue-400/40 bg-blue-500/15 px-4 py-3 text-[13px] text-blue-100"
            data-testid="hotels-readonly-notice"
          >
            <ShieldCheck className="w-4 h-4 mt-0.5 text-blue-300 shrink-0" />
            <div className="font-medium">
              Hotel records are managed by the platform Super Admin. This screen is
              read-only. Configure tax details, floors, rooms, taxes, payment methods and
              business rules from the Hotel Setup Wizard or the relevant Master Data tabs.
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {hotels.map((h) => (
              <Card key={h.id} className="p-5 hover:border-hocp-border transition-colors" data-testid={`hotel-card-${h.code}`}>
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded bg-hocp-bgSubtle border border-hocp-border flex items-center justify-center shrink-0"><Building2 className="w-4 h-4 text-hocp-textMuted" strokeWidth={1.5}/></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-heading text-[15px] text-white font-semibold truncate">{h.name}</div>
                      <Badge tone={h.is_active ? "success" : "neutral"}>{h.is_active ? "ACTIVE" : "DISABLED"}</Badge>
                    </div>
                    <div className="mono text-[11px] text-hocp-textMuted mt-0.5">{h.code} · {h.timezone}</div>
                    {h.address && <div className="text-[12px] text-hocp-textMuted mt-2 line-clamp-2">{h.address}</div>}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="departments" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={deptOpen} onOpenChange={setDeptOpen}>
              <DialogTrigger asChild><Button className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold h-9 px-4 text-[13px]" data-testid="create-department-button"><Plus className="w-3.5 h-3.5 mr-1.5"/> New department</Button></DialogTrigger>
              <DialogContent className="bg-hocp-bgSubtle border-hocp-border">
                <DialogHeader><DialogTitle className="font-heading">Create department</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Name</Label><Input value={deptForm.name} onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })} className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="department-name-input"/></div>
                  <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Code</Label><Input value={deptForm.code} onChange={(e) => setDeptForm({ ...deptForm, code: e.target.value.toUpperCase().replace(/\s+/g, "_") })} className="bg-hocp-bgCard border-hocp-border mt-1.5 mono" data-testid="department-code-input"/></div>
                  <div><Label className="text-xs uppercase tracking-wider text-hocp-textMuted">Hotel</Label>
                    <Select value={deptForm.hotel_id} onValueChange={(v) => setDeptForm({ ...deptForm, hotel_id: v })}><SelectTrigger className="bg-hocp-bgCard border-hocp-border mt-1.5" data-testid="department-hotel-select"><SelectValue placeholder="Select hotel"/></SelectTrigger>
                    <SelectContent>{hotels.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}</SelectContent></Select>
                  </div>
                </div>
                <DialogFooter><Button onClick={createDept} className="bg-hocp-mint text-hocp-deepTeal hover:bg-hocp-mintStrong font-semibold" data-testid="submit-department-button">Create</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <DataTable
            testid="departments-table"
            columns={[
              { key: "name", label: "Name" },
              { key: "code", label: "Code", mono: true },
              { key: "hotel", label: "Hotel", render: (d) => hotels.find((h) => h.id === d.hotel_id)?.name || "—" },
              { key: "actions", label: "", align: "right", render: (d) => <Button size="sm" variant="ghost" onClick={() => removeDept(d.id)} className="h-7 w-7 p-0 text-red-400" data-testid={`delete-department-${d.code}`}><Trash2 className="w-3.5 h-3.5"/></Button> },
            ]}
            rows={depts}
            empty="No departments yet."
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
